import {
  Autocomplete,
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PageSpeedPerformanceTabs from "./PageSpeedPerformanceTabs";
import Page404 from "../../../Page404/Page403";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../features/userSlice";
import FloatingActionItems from "../../../../pages/FloatingItems/FloatingActionItems";
import { useDispatch } from "react-redux";
import { setSelectedPage, setSelectedSharedUid } from "../../../../features/pageSpeedPerformanceSlice";

function PageSpeedPerformance() {
  const { selectedSharedUid } = useSelector(
    (state: any) => state.pageSpeedPerformance
  );
  const dispatch = useDispatch();
  const [showPageSpeedPerformanceTabs, setShowPageSpeedPerformanceTabs] =
    useState(false);
  const response = useSelector(selectUserData);
  const siteOptions =
    response?.data?.website_permissions?.map((siteObj: any) => {
      const uid = Object.keys(siteObj)[0]; // UID of the site
      const siteName = siteObj[uid].ui_site_name; // Site name
      return { uid, siteName }; // Return as an object with uid and siteName
    }) || [];
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const handleSelectSite = () => {
    setShowPageSpeedPerformanceTabs(true);
    if (selectedSharedUid) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete("deviceTab1");
      updatedParams.delete("intervalTab1");
      updatedParams.delete("quarterTab1");
      updatedParams.delete("selectedPeriodTab1");
      updatedParams.delete("filters");
      updatedParams.delete("pageScanPage");
      updatedParams.delete("tab");
      updatedParams.delete("search");
      updatedParams.delete("quarterlyGoalsPage");
      updatedParams.set("selectedSite", selectedSharedUid);
      updatedParams.set("tab", "0");
      setSearchParams(updatedParams);
    }
  };

  const hasViewPageSpeedPerformancePermission = CheckPermissions(
    response?.data?.products?.assets,
    "health",
    "view_page_speed_performance"
  );

  useEffect(() => {
    const siteUidFromParams = searchParams.get("selectedSite");
    if (siteUidFromParams) {
      const selectedSite = siteOptions.find(
        (site: any) => site.uid === siteUidFromParams
      );
      if (selectedSite) {
        dispatch(setSelectedSharedUid(selectedSite.uid));
        setShowPageSpeedPerformanceTabs(true);
      } else {
        navigate("/operations/page_speed_performance", { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <>
      {hasViewPageSpeedPerformancePermission ? (
        <>
          <Paper sx={{ padding: "8px 16px", mb: 2 }}>
            <Typography fontSize={24}>Page Speed Performance</Typography>
            <Typography sx={{ color: "#00000099", paddingBottom: "16px" }}>
              Get your PageSpeed score and improve your site’s performance
            </Typography>
            <Grid container mt={0} columnSpacing={3} mb={1}>
              <Grid item xs={10}>
                <Autocomplete
                  disablePortal
                  sx={{ pt: "8px", width: "100%" }}
                  options={siteOptions}
                  value={
                    siteOptions.find(
                      (site: any) => site.uid === selectedSharedUid
                    ) || null
                  }
                  getOptionLabel={(option: any) => option.siteName} // Display siteName in the Autocomplete
                  onChange={(_, newValue) => {
                    dispatch(setSelectedSharedUid(newValue?.uid || null)); // Store the UID of the selected site
                    setShowPageSpeedPerformanceTabs(false); // Set to false when value changes
                  }}
                  onInputChange={(_, __, reason) => {
                    if (reason === "clear") {
                      setShowPageSpeedPerformanceTabs(false); // Set to false when the clear button (cross) is clicked
                      dispatch(setSelectedSharedUid(null));
                      dispatch(setSelectedPage(null));
                      const updatedParams = new URLSearchParams(searchParams);
                      updatedParams.delete("selectedSite");
                      updatedParams.set("tab", "0");
                      updatedParams.delete("deviceTab1");
                      updatedParams.delete("intervalTab1");
                      updatedParams.delete("quarterTab1");
                      updatedParams.delete("selectedPeriodTab1");
                      updatedParams.delete("filters");
                      updatedParams.delete("pageScanPage");
                      updatedParams.delete("tab");
                      updatedParams.delete("search");
                      updatedParams.delete("quarterlyGoalsPage");
                      setSearchParams(updatedParams);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search site name or choose from the list"
                      label="Select Website"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  data-tracker="155"
                  variant="contained"
                  size="large"
                  color="secondary"
                  fullWidth
                  onClick={handleSelectSite}
                  disabled={!selectedSharedUid} // Disable if no site is selected or loading
                  sx={{ marginTop: "5px" }}
                >
                  SELECT SITE
                </Button>
              </Grid>
            </Grid>
          </Paper>
          {showPageSpeedPerformanceTabs ? (
            <>
              <Paper sx={{ padding: "8px 16px" }}>
                <PageSpeedPerformanceTabs />
              </Paper>
              <div data-tracker="401">
                {selectedSharedUid ? (
                  <FloatingActionItems
                    selectedSiteFloatingAction={selectedSharedUid}
                    selectedDimensionsFloatingAction={""}
                    trackIdFloatingAction={"431"}
                  />
                ) : (
                  ""
                )}
              </div>
            </>
          ) : (
            ""
          )}
        </>
      ) : (
        <Page404 />
      )}
    </>
  );
}

export default PageSpeedPerformance;
